import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import AttachmentList from "@/components/AttachmentList";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { FileDown, Filter, ShieldCheck, MessageCircle, CheckCircle2, ExternalLink, Copy, Send, ClipboardList, AlertTriangle } from "lucide-react";

const STATUS_LABEL = { received: "Recebida", triage: "Em triagem", investigation: "Em investigação", closed: "Concluída" };
const STATUS_COLOR = {
  received: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  triage: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  investigation: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  closed: "bg-teal-500/15 text-teal-300 border-teal-500/40",
};

const COLORS = ["#4F46E5", "#0D9488", "#F97316", "#EAB308", "#EF4444", "#3B82F6", "#10B981", "#A855F7"];

export default function GestorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/gestor/stats"),
        api.get("/gestor/complaints" + (filter !== "all" ? `?status=${filter}` : "")),
      ]);
      setStats(s.data);
      setItems(l.data.items);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (p) => {
    try {
      const r = await api.get(`/gestor/complaints/${p.protocol}`);
      setSelected(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const changeStatus = async (status, classification, corrective) => {
    try {
      await api.post(`/gestor/complaints/${selected.protocol}/status`, {
        status,
        note: `Status atualizado para ${STATUS_LABEL[status]}`,
        classification,
        corrective_actions: corrective,
      });
      toast.success("Status atualizado");
      const r = await api.get(`/gestor/complaints/${selected.protocol}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await api.post(`/gestor/complaints/${selected.protocol}/messages`, { text: msg });
      setMsg("");
      const r = await api.get(`/gestor/complaints/${selected.protocol}`);
      setSelected(r.data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportReport = async () => {
    try {
      const r = await api.get("/gestor/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `vixvox-pgr-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      toast.success("Relatório PGR exportado");
    } catch (e) { toast.error(apiError(e)); }
  };

  const publicUrl = `${window.location.origin}/denunciar/${user?.tenant_slug || ""}`;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-teal-400 mb-1">{user?.tenant_name}</div>
          <h1 className="font-display text-3xl font-bold text-white">Painel de Compliance</h1>
          <p className="text-slate-400 text-sm mt-1">Olá, {user?.name} — {user?.role === "tenant_admin" ? "Administrador" : "Gestor"}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link do canal copiado"); }} data-testid="btn-copy-public-url" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
            <Copy className="w-4 h-4 mr-2" /> Copiar link do canal
          </Button>
          <a href={publicUrl} target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="btn-open-public" className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800">
              <ExternalLink className="w-4 h-4 mr-2" /> Abrir canal público
            </Button>
          </a>
          <Button onClick={exportReport} data-testid="btn-export-pgr" className="bg-teal-600 hover:bg-teal-700 text-white">
            <FileDown className="w-4 h-4 mr-2" /> Exportar PGR
          </Button>
        </div>
      </div>

      <SubscriptionBanner user={user} />

      {/* KPI Cards */}
      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Denúncias totais", value: stats.total, icon: ClipboardList, color: "indigo" },
            { label: "Casos ativos", value: stats.active, icon: AlertTriangle, color: "amber" },
            { label: "Anônimas", value: stats.anonymous, icon: ShieldCheck, color: "teal" },
            { label: "Concluídas", value: stats.by_status?.closed || 0, icon: CheckCircle2, color: "emerald" },
          ].map((k, i) => (
            <Card key={i} data-testid={`kpi-${i}`} className="p-5 bg-slate-900/60 border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-slate-400">{k.label}</div>
                <k.icon className={`w-4 h-4 text-${k.color}-400`} />
              </div>
              <div className="font-display text-3xl font-bold text-white">{k.value}</div>
            </Card>
          ))}
        </div>
      )}

      {/* Charts */}
      {stats && (
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 bg-slate-900/60 border-slate-800">
            <h3 className="font-display font-semibold text-white mb-4">Ocorrências por categoria</h3>
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={stats.by_category}>
                  <XAxis dataKey="category" tick={{ fill: "#94a3b8", fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={70} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", color: "#f8fafc" }} />
                  <Bar dataKey="count" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="p-6 bg-slate-900/60 border-slate-800">
            <h3 className="font-display font-semibold text-white mb-4">Evolução mensal</h3>
            <div className="h-64">
              <ResponsiveContainer>
                <LineChart data={stats.monthly}>
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", color: "#f8fafc" }} />
                  <Line type="monotone" dataKey="count" stroke="#0D9488" strokeWidth={2} dot={{ fill: "#0D9488", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      )}

      {/* Filter + List */}
      <Card className="p-6 bg-slate-900/60 border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-display font-semibold text-white flex items-center gap-2"><Filter className="w-4 h-4 text-teal-400" /> Fila de casos</h3>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-white" data-testid="filter-status-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="received">Recebidas</SelectItem>
              <SelectItem value="triage">Em triagem</SelectItem>
              <SelectItem value="investigation">Em investigação</SelectItem>
              <SelectItem value="closed">Concluídas</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 text-left border-b border-slate-800">
                <th className="py-3 px-2 font-medium">Protocolo</th>
                <th className="py-3 px-2 font-medium">Categoria</th>
                <th className="py-3 px-2 font-medium">Modo</th>
                <th className="py-3 px-2 font-medium">Local</th>
                <th className="py-3 px-2 font-medium">Recebida</th>
                <th className="py-3 px-2 font-medium">Status</th>
                <th className="py-3 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="7" className="py-8 text-center text-slate-500">Nenhuma denúncia neste filtro.</td></tr>
              )}
              {items.map((it) => (
                <tr key={it.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                  <td className="py-3 px-2 font-mono text-teal-300">{it.protocol}</td>
                  <td className="py-3 px-2 text-slate-200">{it.category}</td>
                  <td className="py-3 px-2 text-slate-400">{it.mode === "anonymous" ? "Anônima" : "Identificada"}</td>
                  <td className="py-3 px-2 text-slate-400">{it.location || "—"}</td>
                  <td className="py-3 px-2 text-slate-400">{new Date(it.created_at).toLocaleDateString("pt-BR")}</td>
                  <td className="py-3 px-2"><Badge className={STATUS_COLOR[it.status]}>{STATUS_LABEL[it.status]}</Badge></td>
                  <td className="py-3 px-2 text-right"><Button size="sm" onClick={() => openDetail(it)} data-testid={`btn-open-${it.protocol}`} className="bg-indigo-600 hover:bg-indigo-700 text-white">Abrir</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center justify-between gap-4">
                  <span className="font-mono text-teal-300">{selected.protocol}</span>
                  <Badge className={STATUS_COLOR[selected.status]}>{STATUS_LABEL[selected.status]}</Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                <div className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Categoria</div><div className="text-white">{selected.category}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Modo</div><div className="text-white">{selected.mode === "anonymous" ? "Anônima" : "Identificada"}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Local</div><div className="text-white">{selected.location || "—"}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Setor / GHE</div><div className="text-white">{selected.ghe_sector || "—"}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Data ocorrência</div><div className="text-white">{selected.occurred_at || selected.occurrence_period || "—"}</div></div>
                  <div><div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Envolvidos</div><div className="text-white">{selected.involved || "—"}</div></div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Descrição</div>
                  <div className="p-4 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 whitespace-pre-wrap text-sm">{selected.description}</div>
                </div>

                <AttachmentList attachments={selected.attachments} />

                {selected.reporter && (
                  <div className="p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/30 text-sm">
                    <div className="text-xs uppercase tracking-widest text-indigo-300 mb-2">Denunciante identificado (acesso restrito)</div>
                    <div className="text-white">{selected.reporter.name}</div>
                    <div className="text-slate-300">{selected.reporter.contact}</div>
                  </div>
                )}

                <div>
                  <h4 className="font-semibold text-white mb-3">Atualizar status</h4>
                  <div className="flex flex-wrap gap-2">
                    {["received", "triage", "investigation"].map((s) => (
                      <Button key={s} size="sm" variant={selected.status === s ? "default" : "outline"} onClick={() => changeStatus(s)} data-testid={`btn-status-${s}`} className={selected.status === s ? "bg-indigo-600 text-white" : "border-slate-700 bg-transparent text-slate-100 hover:bg-slate-800"}>
                        {STATUS_LABEL[s]}
                      </Button>
                    ))}
                  </div>
                  {selected.status !== "closed" && (
                    <div className="mt-4 p-4 rounded-lg bg-slate-800 border border-slate-700 space-y-3">
                      <div className="text-xs uppercase tracking-widest text-teal-400">Encerrar caso</div>
                      <div className="flex flex-wrap gap-2">
                        {["procedente", "improcedente", "inconclusiva"].map((c) => (
                          <Button key={c} size="sm" variant="outline" onClick={() => changeStatus("closed", c, "")} data-testid={`btn-close-${c}`} className="border-slate-700 bg-transparent text-slate-100 hover:bg-slate-700 capitalize">
                            Concluir como {c}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-teal-400" /> Chat com denunciante</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto mb-3 p-3 rounded-lg bg-slate-950/60 border border-slate-800">
                    {selected.messages.length === 0 && <div className="text-center text-slate-500 text-sm py-4">Ainda sem mensagens</div>}
                    {selected.messages.map((m) => (
                      <div key={m.id} className={`flex ${m.author_role === "manager" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.author_role === "manager" ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-100 border border-slate-700"}`}>
                          <div className="text-xs opacity-70 mb-1">{m.author_label} · {new Date(m.created_at).toLocaleString("pt-BR")}</div>
                          <div className="whitespace-pre-wrap">{m.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Textarea rows={2} placeholder="Responder ao denunciante…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="gestor-chat-input" className="bg-slate-800 border-slate-700 text-white" />
                    <Button onClick={sendMsg} data-testid="gestor-chat-send" className="bg-indigo-600 hover:bg-indigo-700 text-white"><Send className="w-4 h-4" /></Button>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Linha do tempo</h4>
                  <ol className="relative border-l border-slate-700 pl-6 space-y-3 text-sm">
                    {selected.timeline.map((t, i) => (
                      <li key={i}>
                        <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-teal-500" />
                        <div className="text-white">{STATUS_LABEL[t.status] || t.status}</div>
                        {t.note && <div className="text-xs text-slate-400">{t.note}</div>}
                        <div className="text-xs text-slate-500">{new Date(t.at).toLocaleString("pt-BR")}{t.by ? ` · ${t.by}` : ""}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
